import {
  parseRaMaster,
  RA_ASSESSMENT_TYPES,
  RA_CONTROL_HIERARCHY,
  RA_CONTROL_NATURES,
  RA_CONTROL_REQUIREMENTS,
  RA_CONTROL_STAGES,
  RA_HAZARD_CATEGORIES,
  RA_MASTER_STATUSES,
  RA_ROW_CONTROL_STATUSES,
  RA_VESSEL_GROUPS,
  type RaMasterKind,
  type RaMatrixDefinition,
  raLinkRatings,
  suggestRaHazardCategory,
} from '@felix/domain';
import {
  ActionIcon,
  Button,
  Drawer,
  MultiSelect,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import purchasing from '../../purchasing/Purchasing.module.css';
import { LevelBadge } from '../RiskParts.js';
import { type ActivityReviewInput, ActivityReviewPanel } from './FinalReview.js';
import { MasterStatus } from './MasterTables.js';
import classes from './RaMaster.module.css';
import type { RaMasterItem, RaMasterView } from './types.js';
import { indexOf, linkTitle } from './view.js';

/**
 * Master Data kaydının çekmecesi (K2; ADR 0086 §7): ofiste yeni kayıt ve düzenleme, gemide salt
 * okunur ayrıntı. İçerik domain şemasıyla (`parseRaMaster`) denetlenir; sunucu aynı şemayla ve
 * tablo kurallarıyla yeniden denetler. Kod yalnız yeni kayıtta yazılır.
 */

type Content = Record<string, unknown>;

const SCALE = ['1', '2', '3', '4', '5'];

/** Yeni kaydın boş içeriği. */
export function emptyContent(kind: RaMasterKind, view: RaMasterView): Content {
  switch (kind) {
    case 'categories':
      return { code: '', name: '', vesselGroups: ['ALL'], status: 'draft' };
    case 'activities':
      return {
        code: '',
        categoryId: view.categories[0]?.id ?? '',
        name: '',
        scope: null,
        steps: [{ id: 'S1', text: '' }],
        vesselGroups: ['ALL'],
        assessmentTypes: [],
        ownerRole: null,
        status: 'draft',
      };
    case 'consequences':
      return { code: '', name: '', status: 'draft' };
    case 'hazards':
      return {
        code: '',
        family: null,
        name: '',
        category: null,
        mechanism: null,
        consequenceIds: [],
        exposed: null,
        status: 'draft',
      };
    case 'controls':
      return {
        code: '',
        hazardId: null,
        stage: 'existing',
        measure: '',
        hierarchy: ['administrative'],
        nature: null,
        requirement: null,
        verificationMethod: null,
        evidence: null,
        responsibleRole: null,
        status: 'draft',
      };
    case 'links':
      return {
        code: '',
        activityId: view.activities[0]?.id ?? '',
        stepId: null,
        hazardId: view.hazards[0]?.id ?? '',
        likelihood: 3,
        severity: 3,
        residualLikelihood: null,
        residualSeverity: null,
        existingControlIds: [],
        additionalControlIds: [],
        controlStatus: 'not_verified',
        evidence: null,
        exposed: null,
        status: 'draft',
      };
  }
}

// `final`: faaliyetin FINAL alanları içerik değildir (ADR 0098); inceleme ayrı işlemdir
const META = new Set(['id', 'rowVersion', 'updatedAt', 'final']);
const contentOf = (item: RaMasterItem<RaMasterKind>): Content =>
  Object.fromEntries(Object.entries(item).filter(([key]) => !META.has(key)));

export interface MasterDrawerProps {
  kind: RaMasterKind;
  item: RaMasterItem<RaMasterKind> | null;
  view: RaMasterView;
  matrix: RaMatrixDefinition;
  editable: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave?: (content: Content) => void;
  onStatus?: (status: 'active' | 'retired') => void;
  /** FINAL faaliyetinin kıyı teknik incelemesi (ADR 0098 §5); yalnız onay yetkisiyle. */
  onReview?: (input: ActivityReviewInput) => void;
}

export function MasterDrawer(props: MasterDrawerProps) {
  const { t } = useTranslation();
  const { kind, item, view, editable } = props;
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskMaster.form.${key}`, options);
  const [draft, setDraft] = useState<Content>(() =>
    item ? contentOf(item) : emptyContent(kind, view),
  );
  const [issues, setIssues] = useState<Record<string, string>>({});
  const set = (patch: Content) => setDraft((d) => ({ ...d, ...patch }));
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  const optional = (value: string) => (value.trim() === '' ? null : value);
  const error = (field: string) =>
    issues[field] ? t(`riskMaster.issues.${issues[field]}`, { defaultValue: issues[field] }) : null;
  const index = indexOf(view);
  const title = item
    ? kind === 'links'
      ? linkTitle(view, item as RaMasterItem<'links'>)
      : `${text(draft.code)} · ${text(draft.name ?? draft.measure)}`
    : k('newTitle', { kind: t(`riskMaster.tabs.${kind}`) });

  const save = () => {
    const parsed = parseRaMaster(kind, draft);
    if (!parsed.ok) {
      setIssues(Object.fromEntries(parsed.issues.map((i) => [i.path.split('.')[0] ?? '', i.code])));
      return;
    }
    setIssues({});
    props.onSave?.(parsed.value as Content);
  };

  const option = (list: readonly string[], prefix: string) =>
    list.map((value) => ({ value, label: t(`riskMaster.${prefix}.${value}`) }));
  const select = (
    field: string,
    label: string,
    data: { value: string; label: string }[],
    options: { clearable?: boolean; className?: string | undefined } = {},
  ) => (
    <Select
      label={label}
      className={options.className}
      value={draft[field] === null || draft[field] === undefined ? null : String(draft[field])}
      data={data}
      readOnly={!editable}
      clearable={options.clearable === true && editable}
      allowDeselect={options.clearable === true}
      searchable={data.length > 8}
      error={error(field)}
      onChange={(v) => set({ [field]: v ?? null })}
    />
  );
  const multi = (
    field: string,
    label: string,
    data: { value: string; label: string }[],
    description?: string,
  ) => (
    <MultiSelect
      className={classes.span2}
      label={label}
      description={description}
      value={(draft[field] as string[] | undefined) ?? []}
      data={data}
      readOnly={!editable}
      searchable={data.length > 8}
      error={error(field)}
      onChange={(v) => set({ [field]: v })}
    />
  );
  const input = (
    field: string,
    label: string,
    options: {
      max: number;
      required?: boolean;
      span?: boolean;
      help?: string | undefined;
      readOnly?: boolean;
    },
  ) => (
    <TextInput
      className={options.span ? classes.span2 : undefined}
      label={label}
      description={options.help}
      required={options.required === true}
      readOnly={!editable || options.readOnly === true}
      maxLength={options.max}
      value={text(draft[field])}
      error={error(field)}
      onChange={(e) =>
        set({ [field]: options.required ? e.currentTarget.value : optional(e.currentTarget.value) })
      }
    />
  );
  const area = (field: string, label: string, max: number, help?: string) => (
    <Textarea
      className={classes.span2}
      label={label}
      description={help}
      readOnly={!editable}
      maxLength={max}
      autosize
      minRows={2}
      maxRows={6}
      value={text(draft[field])}
      error={error(field)}
      onChange={(e) => set({ [field]: optional(e.currentTarget.value) })}
    />
  );

  const codeField = input('code', k('code'), {
    max: 40,
    required: true,
    readOnly: item !== null,
    help: item ? undefined : k('codeHelp'),
  });
  const statusField = select('status', k('status'), option(RA_MASTER_STATUSES, 'statuses'));
  const groupsField = multi('vesselGroups', k('groups'), option(RA_VESSEL_GROUPS, 'groups'));

  let fields: ReactNode = null;
  if (kind === 'categories') {
    fields = (
      <>
        {codeField}
        {statusField}
        {input('name', k('name'), { max: 120, required: true, span: true })}
        {groupsField}
      </>
    );
  } else if (kind === 'activities') {
    const steps = (draft.steps as { id: string; text: string }[]) ?? [];
    const nextId = () => {
      let n = steps.length + 1;
      while (steps.some((s) => s.id === `S${n}`)) n += 1;
      return `S${n}`;
    };
    fields = (
      <>
        {codeField}
        {statusField}
        {input('name', k('name'), { max: 200, required: true, span: true })}
        {select(
          'categoryId',
          k('category'),
          view.categories.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` })),
          { className: classes.span2 },
        )}
        {area('scope', k('scope'), 2000)}
        <fieldset className={`${classes.span2} ${purchasing.dialogSection}`}>
          <legend>{k('steps')}</legend>
          <div className={classes.steps}>
            {steps.map((step, i) => (
              <div key={step.id} className={classes.stepRow}>
                <TextInput
                  label={k('stepN', { n: i + 1 })}
                  readOnly={!editable}
                  maxLength={300}
                  value={step.text}
                  error={i === 0 ? error('steps') : null}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    set({
                      steps: steps.map((s) => (s.id === step.id ? { ...s, text: value } : s)),
                    });
                  }}
                />
                {editable && steps.length > 1 ? (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={k('removeStep', { n: i + 1 })}
                    onClick={() => set({ steps: steps.filter((s) => s.id !== step.id) })}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                ) : null}
              </div>
            ))}
            {editable ? (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => set({ steps: [...steps, { id: nextId(), text: '' }] })}
              >
                {k('addStep')}
              </Button>
            ) : null}
          </div>
        </fieldset>
        {groupsField}
        {multi('assessmentTypes', k('types'), option(RA_ASSESSMENT_TYPES, 'assessmentTypes'))}
        {input('ownerRole', k('ownerRole'), { max: 120, span: true })}
      </>
    );
  } else if (kind === 'consequences') {
    fields = (
      <>
        {codeField}
        {statusField}
        {input('name', k('name'), { max: 200, required: true, span: true })}
      </>
    );
  } else if (kind === 'hazards') {
    const suggestion = draft.category ? null : suggestRaHazardCategory(text(draft.name));
    fields = (
      <>
        {codeField}
        {statusField}
        {input('name', k('name'), { max: 300, required: true, span: true })}
        {input('family', k('family'), { max: 40 })}
        <div>
          {select(
            'category',
            k('hazardCategory'),
            option(RA_HAZARD_CATEGORIES, 'hazardCategories'),
            {
              clearable: true,
            },
          )}
          {suggestion ? (
            <p className={classes.hint}>
              {k('suggested', { category: t(`riskMaster.hazardCategories.${suggestion}`) })}{' '}
              {editable ? (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => set({ category: suggestion })}
                >
                  {k('useSuggestion')}
                </Button>
              ) : null}
            </p>
          ) : null}
        </div>
        {area('mechanism', k('mechanism'), 1000)}
        {multi(
          'consequenceIds',
          k('consequences'),
          view.consequences.map((c) => ({ value: c.id, label: c.name })),
        )}
        {input('exposed', k('exposed'), { max: 300, span: true })}
      </>
    );
  } else if (kind === 'controls') {
    fields = (
      <>
        {codeField}
        {statusField}
        {area('measure', k('measure'), 500)}
        {select(
          'hazardId',
          k('hazard'),
          view.hazards.map((h) => ({ value: h.id, label: `${h.code} · ${h.name}` })),
          { clearable: true, className: classes.span2 },
        )}
        {select('stage', k('stage'), option(RA_CONTROL_STAGES, 'stages'))}
        {select('nature', k('nature'), option(RA_CONTROL_NATURES, 'natures'), { clearable: true })}
        {multi(
          'hierarchy',
          k('hierarchy'),
          option(RA_CONTROL_HIERARCHY, 'hierarchy'),
          k('hierarchyHelp'),
        )}
        {select('requirement', k('requirement'), option(RA_CONTROL_REQUIREMENTS, 'requirements'), {
          clearable: true,
        })}
        {input('responsibleRole', k('responsibleRole'), { max: 120 })}
        {input('verificationMethod', k('verificationMethod'), { max: 500, span: true })}
        {input('evidence', k('evidence'), { max: 500, span: true })}
      </>
    );
  } else {
    const activity = index.activities.get(text(draft.activityId));
    const ratings = raLinkRatings(
      {
        likelihood: draft.likelihood === null ? null : Number(draft.likelihood),
        severity: draft.severity === null ? null : Number(draft.severity),
        residualLikelihood:
          draft.residualLikelihood === null ? null : Number(draft.residualLikelihood),
        residualSeverity: draft.residualSeverity === null ? null : Number(draft.residualSeverity),
      },
      props.matrix,
    );
    const hazardControls = (stage: string) =>
      view.controls
        .filter((c) => c.stage === stage && (c.hazardId === null || c.hazardId === draft.hazardId))
        .map((c) => ({ value: c.id, label: `${c.code} · ${c.measure}` }));
    const scale = (field: string, label: string, clearable: boolean) => (
      <Select
        label={label}
        value={draft[field] === null || draft[field] === undefined ? null : String(draft[field])}
        data={SCALE}
        readOnly={!editable}
        clearable={clearable && editable}
        allowDeselect={clearable}
        error={error(field)}
        onChange={(v) => set({ [field]: v === null ? null : Number(v) })}
      />
    );
    fields = (
      <>
        {codeField}
        {statusField}
        {select(
          'activityId',
          k('activity'),
          view.activities.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` })),
          { className: classes.span2 },
        )}
        {select(
          'stepId',
          k('step'),
          (activity?.steps ?? []).map((s, i) => ({ value: s.id, label: `${i + 1}. ${s.text}` })),
          { clearable: true, className: classes.span2 },
        )}
        {select(
          'hazardId',
          k('hazard'),
          view.hazards.map((h) => ({ value: h.id, label: `${h.code} · ${h.name}` })),
          { className: classes.span2 },
        )}
        <fieldset className={purchasing.dialogSection}>
          <legend>{k('initial')}</legend>
          <div className={classes.pair}>
            {scale('likelihood', k('likelihood'), true)}
            {scale('severity', k('severity'), true)}
          </div>
          <p className={classes.hint}>
            {ratings.initial ? (
              <LevelBadge level={ratings.initial.level} score={ratings.initial.score} />
            ) : (
              // Taslak öneride bilerek boş; etkinleştirmeden önce yetkin kişi girer (ADR 0098 §4)
              t('riskMaster.notAssessed')
            )}
          </p>
        </fieldset>
        <fieldset className={purchasing.dialogSection}>
          <legend>{k('residual')}</legend>
          <div className={classes.pair}>
            {scale('residualLikelihood', k('likelihood'), true)}
            {scale('residualSeverity', k('severity'), true)}
          </div>
          <p className={classes.hint}>
            {ratings.residual ? (
              <LevelBadge level={ratings.residual.level} score={ratings.residual.score} />
            ) : (
              t('riskMaster.notAssessed')
            )}
          </p>
        </fieldset>
        <p className={`${classes.hint} ${classes.span2}`}>{k('residualHelp')}</p>
        {multi('existingControlIds', k('existingControls'), hazardControls('existing'))}
        {multi('additionalControlIds', k('additionalControls'), hazardControls('additional'))}
        {select(
          'controlStatus',
          k('controlStatus'),
          option(RA_ROW_CONTROL_STATUSES, 'controlStatuses'),
        )}
        {input('exposed', k('exposed'), { max: 300 })}
        {input('evidence', k('evidence'), { max: 500, span: true })}
      </>
    );
  }

  const status = text(item ? (item as unknown as Content).status : draft.status);
  return (
    <Drawer
      opened
      onClose={props.onClose}
      position="right"
      size="lg"
      title={title}
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      {!editable ? <p className={classes.note}>{k('readOnly')}</p> : null}
      {item ? (
        <p className={classes.hint}>
          <MasterStatus status={status} t={t} />
        </p>
      ) : null}
      <div className={classes.form}>{fields}</div>
      {kind === 'activities' && item && (item as RaMasterItem<'activities'>).final ? (
        <ActivityReviewPanel
          item={item as RaMasterItem<'activities'>}
          busy={props.busy}
          {...(props.onReview ? { onReview: props.onReview } : {})}
        />
      ) : null}
      {props.error ? (
        <p className={purchasing.error} role="alert">
          {props.error}
        </p>
      ) : null}
      <div className={classes.drawerActions}>
        {editable && item && props.onStatus ? (
          status === 'retired' ? (
            <Button
              data-left
              variant="light"
              onClick={() => props.onStatus?.('active')}
              loading={props.busy}
            >
              {t('riskMaster.actions.reactivate')}
            </Button>
          ) : (
            <Button
              data-left
              variant="light"
              color="red"
              onClick={() => props.onStatus?.('retired')}
              loading={props.busy}
            >
              {t('riskMaster.actions.retire')}
            </Button>
          )
        ) : null}
        <Button variant="default" onClick={props.onClose}>
          {editable ? k('cancel') : k('close')}
        </Button>
        {editable ? (
          <Button onClick={save} loading={props.busy}>
            {k('save')}
          </Button>
        ) : null}
      </div>
    </Drawer>
  );
}
